import type { Lesson } from "./types";
import { getCursor, getLastCourseId, type ProgressState } from "./progress";
import { COURSES } from "../data/courses";
import { LESSON_COMMITS } from "../data/lessons/commits-branches";
import { LESSON_FOUNDATIONS } from "../data/lessons/foundations";
import { LESSON_MERGE_CONFLICTS } from "../data/lessons/merge-conflicts";
import { LESSON_PULL_REQUESTS } from "../data/lessons/pull-requests";
import { LESSON_REMOTE } from "../data/lessons/remote";
import { LESSON_UNDOING } from "../data/lessons/undoing";

/** Every lesson keyed by course id, for resolving titles and step labels. */
export const LESSONS_BY_ID: Record<string, Lesson> = Object.fromEntries(
  [
    LESSON_FOUNDATIONS,
    LESSON_COMMITS,
    LESSON_REMOTE,
    LESSON_PULL_REQUESTS,
    LESSON_MERGE_CONFLICTS,
    LESSON_UNDOING,
  ].map((l) => [l.courseId, l]),
);

// Single source of truth for "how many steps does each course have", derived
// from the lesson data itself so it can't drift. A course is complete when the
// learner has visited (= recorded done) every one of its steps — the same
// definition the lesson screens use when marking the final step done.
export const COURSE_STEP_COUNTS: Record<string, number> = Object.fromEntries(
  [
    LESSON_FOUNDATIONS,
    LESSON_COMMITS,
    LESSON_REMOTE,
    LESSON_PULL_REQUESTS,
    LESSON_MERGE_CONFLICTS,
    LESSON_UNDOING,
  ].map((l) => [l.courseId, l.steps.length]),
);

/** Fraction (0..1) of a course's steps the learner has completed. */
export function courseProgress(
  progress: ProgressState,
  courseId: string,
): number {
  const total = COURSE_STEP_COUNTS[courseId];
  if (!total) return 0;
  const done = progress.steps[courseId]?.length ?? 0;
  return Math.min(1, done / total);
}

/** Whether the learner has explicitly marked a course complete. */
export function isCourseComplete(
  progress: ProgressState,
  courseId: string,
): boolean {
  return !!progress.completed?.[courseId];
}

/**
 * Course availability gate. Sequential gating (unlock a course only once the
 * previous one is marked complete) has been removed — every course is open to
 * the learner regardless of progress. Admin locks are layered on top of this
 * separately, so an instructor can still lock individual courses.
 *
 * Kept as a function (rather than deleted) so the dashboard and "start next
 * course" CTAs keep a single availability check to call.
 */
export function isCourseUnlocked(
  _progress: ProgressState,
  _courseId: string,
): boolean {
  return true;
}

/** The course immediately before `courseId` in the track, if any. */
export function previousCourse(courseId: string) {
  const idx = COURSES.findIndex((c) => c.id === courseId);
  return idx > 0 ? COURSES[idx - 1] : undefined;
}

/** Courses the learner has explicitly marked complete. */
export function completedCourseCount(progress: ProgressState): number {
  return Object.keys(COURSE_STEP_COUNTS).filter((id) =>
    isCourseComplete(progress, id),
  ).length;
}

/** Courses started but not yet complete. */
export function inProgressCourseCount(progress: ProgressState): number {
  return Object.keys(COURSE_STEP_COUNTS).filter((id) => {
    const pct = courseProgress(progress, id);
    return pct > 0 && pct < 1;
  }).length;
}

export const TOTAL_COURSE_COUNT = Object.keys(COURSE_STEP_COUNTS).length;

export interface ResumeTarget {
  courseId: string;
  stepIdx: number;
  courseTitle: string;
  /** Short label of the step to resume on, if resolvable. */
  stepTitle?: string;
  /** Whether the learner has actually started anything (vs. a fresh default). */
  started: boolean;
}

/**
 * Where the dashboard "Resume" card should send the learner: the course they
 * were last on (and the step within it), falling back to the first in-progress
 * course, then the first course in the track for a brand-new learner.
 */
export function resumeTarget(progress: ProgressState): ResumeTarget {
  const order = COURSES.map((c) => c.id);
  const lastId = getLastCourseId(progress);
  const firstInProgress = order.find((id) => {
    const pct = courseProgress(progress, id);
    return pct > 0 && pct < 1;
  });
  const started = !!lastId || !!firstInProgress;
  const courseId = lastId ?? firstInProgress ?? order[0] ?? "foundations";

  const lesson = LESSONS_BY_ID[courseId];
  const maxIdx = lesson ? lesson.steps.length - 1 : 0;
  const stepIdx = Math.min(Math.max(getCursor(progress, courseId), 0), maxIdx);
  const course = COURSES.find((c) => c.id === courseId);

  return {
    courseId,
    stepIdx,
    courseTitle: course?.title ?? lesson?.courseTitle ?? courseId,
    stepTitle: lesson?.steps[stepIdx]?.shortTitle,
    started,
  };
}
